import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
} from "reactstrap";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";
import Breadcrumb from "../../components/Common/Breadcrumb";

import avatar from "../../assets/images/users/avatar-1.jpg";
import { PLATFORM_NAME } from "../../CommonData/Data/constant";
import callApi from "../../helpers/CallApi";
import { updateProfile } from "../../helpers/API";
import { toast } from "react-toastify";

const UserProfile = ({ t }) => {
  // Dynamic document title using translation
  document.title = t("document.title.profile", { PLATFORM_NAME });

  // Single state to manage all user data
  const [userState, setUserState] = useState({
    name: "",
    email: "",
    mobile: "",
    id: null,
  });

  // Initialize state from localStorage on component mount
  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserState({
          name: parsedUser?.name || "",
          email: parsedUser?.email || "",
          mobile: parsedUser?.mobile || parsedUser?.mobile || "",
          id: parsedUser?.id || null,
        });
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    }
  }, []);

  // Dynamic validation schema using translations
  const getValidationSchema = () => {
    return Yup.object({
      name: Yup.string()
        .min(2, t("profile.form.validation.nameMinLength"))
        .max(50, t("profile.form.validation.nameMaxLength"))
        .required(t("profile.form.validation.nameRequired")),
      email: Yup.string()
        .email(t("profile.form.validation.emailInvalid"))
        .required(t("profile.form.validation.emailRequired")),
      mobile: Yup.string()
        .matches(/^[0-9]{10}$/, t("profile.form.validation.mobileInvalid"))
        .required(t("profile.form.validation.mobileRequired")),
    });
  };

  // Formik validation setup
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: userState.name,
      email: userState.email,
      mobile: userState.mobile,
      id: userState.id,
    },
    validationSchema: getValidationSchema(),
    onSubmit: (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        mobile: values.mobile,
      };
      callApi({
        callback: updateProfile,
        payload,
        onSuccess: ({ message, user }) => {
          toast.success(message);
          localStorage.setItem("user", JSON.stringify(user));
          setUserState({
            name: user.name,
            email: user.email,
            mobile: user.mobile,
            id: user.id,
          });
        },
        onError: (error) => {
          toast.error(error);
        },
      });
    },
  });

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb
            title={PLATFORM_NAME}
            breadcrumbItem={t("profile.page.breadcrumb")}
          />

          {/* Profile Display Card */}
          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <div className="d-flex">
                    <div className="ms-3">
                      <img
                        src={avatar}
                        alt={t("profile.form.alt.userAvatar")}
                        className="avatar-md rounded-circle img-thumbnail"
                      />
                    </div>
                    <div className="flex-grow-1 align-self-center ms-2">
                      <div className="text-muted">
                        <p className="mb-0">
                          {t("profile.display.idLabel", { id: userState.id })}
                        </p>
                        <h5>{userState.name}</h5>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <h4 className="card-title mb-4">{t("profile.form.heading")}</h4>

          {/* Profile Edit Form */}
          <Card>
            <CardBody>
              <Form
                className="form-horizontal"
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <Row>
                  <Col md="6">
                    {/* Name Field */}
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.name")}
                      </Label>
                      <Input
                        name="name"
                        className="form-control"
                        placeholder={t("profile.form.placeholders.enterName")}
                        type="text"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.name || ""}
                        invalid={
                          validation.touched.name && validation.errors.name
                            ? true
                            : false
                        }
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">
                          {validation.errors.name}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>

                  <Col md="6">
                    {/* Email Field */}
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.email")}
                      </Label>
                      <Input
                        name="email"
                        className="form-control"
                        placeholder={t("profile.form.placeholders.enterEmail")}
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.email || ""}
                        invalid={
                          validation.touched.email && validation.errors.email
                            ? true
                            : false
                        }
                      />
                      {validation.touched.email && validation.errors.email ? (
                        <FormFeedback type="invalid">
                          {validation.errors.email}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    {/* Mobile Number Field */}
                    <div className="form-group mb-3">
                      <Label className="form-label required">
                        {t("profile.form.labels.mobileNumber")}
                      </Label>
                      <Input
                        name="mobile"
                        className="form-control"
                        placeholder={t("profile.form.placeholders.enterMobile")}
                        type="tel"
                        maxLength="10"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.mobile || ""}
                        invalid={
                          validation.touched.mobile && validation.errors.mobile
                            ? true
                            : false
                        }
                      />
                      {validation.touched.mobile && validation.errors.mobile ? (
                        <FormFeedback type="invalid">
                          {validation.errors.mobile}
                        </FormFeedback>
                      ) : null}
                    </div>
                  </Col>

                  <Col md="6">
                    {/* User ID Display (Read-only) */}
                    <div className="form-group mb-3">
                      <Label className="form-label">
                        {t("profile.form.labels.userId")}
                      </Label>
                      <Input
                        className="form-control"
                        value={`#${userState.id}`}
                        disabled
                        readOnly
                      />
                    </div>
                  </Col>
                </Row>

                {/* Hidden ID field for form submission */}
                <Input name="id" value={validation.values.id} type="hidden" />

                <div className="text-center mt-4">
                  <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    disabled={!validation.isValid}
                  >
                    <i className="ri-save-line me-2"></i>
                    {t("profile.form.buttons.updateProfile")}
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default withTranslation()(withRouter(UserProfile));
